// Vuex module for the bet slip: the legs the user has picked, the stake, the last priced
// response from POST /slip/price and whether the docked slip is open.
// Legs and stake survive a reload in localStorage.NGBetslip; the open state in NGBetslipOpen.
import API from "../api";
import { trackFeature } from "../analytics";
import { snapshotFromPriced, mergeBuilt, buildNotes } from "../components/betslip/buildSlip";

export const MAX_LEGS = 10;
const STORAGE_KEY = "NGBetslip";
const OPEN_KEY = "NGBetslipOpen";

function readStored() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (saved && Array.isArray(saved.legs)) {
      return {
        legs: saved.legs.slice(0, MAX_LEGS),
        stake: Number(saved.stake) || 10,
      };
    }
  } catch (e) {
    // fall through to an empty slip
  }
  return { legs: [], stake: 10 };
}

const VIEW_KEY = "NGPropView";

// What the player-table columns show: our % (default), bet365 odds with our fair odds, or value.
function readView() {
  try {
    const view = localStorage.getItem(VIEW_KEY);
    return ["pct", "odds", "value"].includes(view) ? view : "pct";
  } catch (e) {
    return "pct";
  }
}

function readOpen() {
  try {
    const open = localStorage.getItem(OPEN_KEY);
    if (open === "peek") return "peek";
    return open === "true";
  } catch (e) {
    return false;
  }
}

function persist(state) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ legs: state.legs, stake: state.stake }),
    );
  } catch (e) {
    // private mode etc.: the slip still works for this visit
  }
}

function auth(rootGetters) {
  const token = rootGetters.accessTokens && rootGetters.accessTokens.id_token;
  return token ? { idToken: token } : null;
}

let repriceTimer = null;
let justAddedTimer = null;

const stored = readStored();

export default {
  namespaced: true,
  state: {
    legs: stored.legs,
    stake: stored.stake,
    priced: null,
    pricing: false,
    error: "",
    open: stored.legs.length ? readOpen() : false,
    justAdded: null,
    // what the last build took off the slip and why; gone once the slip changes by hand
    buildNotes: [],
    propView: readView(),
  },
  getters: {
    count: (state) => state.legs.length,
    has: (state) => (id) => state.legs.some((leg) => leg.id === id),
    ids: (state) => state.legs.map((leg) => leg.id),
    pricedLeg: (state) => (id) =>
      state.priced && state.priced.legs
        ? state.priced.legs.find((leg) => leg.id === id) || null
        : null,
    member: (state, getters, rootState, rootGetters) =>
      rootGetters.subscriptionType === "active",
    // the server decides: free accounts get Premier League legs priced and the rest locked;
    // before the first price comes back, assume locked for non-members
    locked: (state, getters) =>
      state.priced ? !!state.priced.locked : !getters.member,
    legLocked: (state, getters) => (id) => {
      const leg = getters.pricedLeg(id);
      return leg ? !!leg.locked : !getters.member;
    },
    visible: (state) => state.legs.length > 0 || !!state.open,
  },
  mutations: {
    setPropView(state, view) {
      state.propView = view;
      try {
        localStorage.setItem(VIEW_KEY, view);
      } catch (e) {
        // private mode: the choice lasts for this visit only
      }
    },
    setLegs(state, legs) {
      state.legs = legs.slice(0, MAX_LEGS);
      persist(state);
    },
    setBuildNotes(state, notes) {
      state.buildNotes = notes;
    },
    addLeg(state, leg) {
      state.buildNotes = [];
      state.legs = [leg, ...state.legs];
      persist(state);
    },
    removeLeg(state, id) {
      state.buildNotes = [];
      state.legs = state.legs.filter((leg) => leg.id !== id);
      persist(state);
    },
    replaceLeg(state, { oldId, leg }) {
      state.buildNotes = [];
      state.legs = state.legs.map((l) => (l.id === oldId ? leg : l));
      persist(state);
    },
    setStake(state, stake) {
      state.stake = stake;
      persist(state);
    },
    setPriced(state, priced) {
      state.priced = priced;
    },
    setPricing(state, pricing) {
      state.pricing = pricing;
    },
    setError(state, error) {
      state.error = error;
    },
    setOpen(state, open) {
      state.open = open;
      try {
        localStorage.setItem(OPEN_KEY, String(open));
      } catch (e) {
        // ignore
      }
    },
    setJustAdded(state, id) {
      state.justAdded = id;
    },
  },
  actions: {
    // Add a leg, or take it off if it is already on the slip (the pill is a toggle).
    toggle({ state, getters, commit, dispatch }, leg) {
      if (getters.has(leg.id)) {
        return dispatch("remove", leg.id);
      }
      // a started match can't be bet on: say so now, not after the slip is priced
      if (leg.kickoff && leg.kickoff * 1000 <= Date.now()) {
        commit("setError", "That match has started, so it can't go on a slip.");
        return "started";
      }
      if (state.legs.length >= MAX_LEGS) {
        commit("setError", `A slip can hold ${MAX_LEGS} legs.`);
        commit("setOpen", true);
        return "full";
      }
      commit("setError", "");
      commit("addLeg", {
        id: leg.id,
        label: leg.label,
        match: leg.match || "",
        match_id: leg.match_id || Number(String(leg.id).split("|")[0]),
        kickoff: leg.kickoff || null,
        side: leg.side || null,
      });
      commit("setJustAdded", leg.id);
      clearTimeout(justAddedTimer);
      justAddedTimer = setTimeout(() => commit("setJustAdded", null), 1500);
      const mobile = window.matchMedia
        ? window.matchMedia("(max-width: 1023px)").matches
        : false;
      if (!state.open || state.open === "peek") {
        commit("setOpen", mobile ? "peek" : true);
      }
      trackFeature("betslip_add", "betslip", {
        market: String(leg.id).split("|")[1],
        legs: state.legs.length,
      });
      dispatch("reprice");
      return "added";
    },
    remove({ state, commit, dispatch }, id) {
      commit("removeLeg", id);
      trackFeature("betslip_remove", "betslip", { legs: state.legs.length });
      if (!state.legs.length) {
        commit("setPriced", null);
        commit("setOpen", false);
        return "removed";
      }
      dispatch("reprice");
      return "removed";
    },
    clear({ commit }) {
      commit("setBuildNotes", []);
      commit("setLegs", []);
      commit("setPriced", null);
      commit("setError", "");
    },
    setStake({ commit, dispatch }, stake) {
      const value = Math.max(0, Math.min(10000, Number(stake) || 0));
      commit("setStake", value);
      dispatch("reprice");
    },
    swap({ commit, dispatch }, { oldId, suggestion, leg }) {
      commit("replaceLeg", {
        oldId,
        leg: {
          id: suggestion.id,
          label: suggestion.label,
          match: leg.match,
          match_id: leg.match_id,
          kickoff: leg.kickoff,
          side: leg.side,
        },
      });
      commit("setJustAdded", suggestion.id);
      clearTimeout(justAddedTimer);
      justAddedTimer = setTimeout(() => commit("setJustAdded", null), 1500);
      dispatch("reprice");
    },
    open({ commit }, open) {
      commit("setOpen", open);
      if (open) trackFeature("betslip_open", "betslip");
    },
    // Debounced POST /slip/price; keeps the leg labels in step with the server's.
    reprice({ state, commit, rootGetters }) {
      clearTimeout(repriceTimer);
      return new Promise((resolve) => {
        repriceTimer = setTimeout(async () => {
          const credentials = auth(rootGetters);
          if (!state.legs.length || !credentials) {
            commit("setPriced", null);
            resolve(null);
            return;
          }
          commit("setPricing", true);
          try {
            const response = await API.post("/slip/price", credentials, {
              selections: state.legs.map((leg) => leg.id),
              stake: state.stake,
            });
            const priced = response.data;
            commit("setPriced", priced);
            commit("setError", "");
            // fill in labels for legs that came from a link or an older snapshot
            const byId = {};
            (priced.legs || []).forEach((leg) => {
              byId[leg.id] = leg;
            });
            const legs = state.legs.map((leg) =>
              byId[leg.id] && (!leg.label || !leg.match)
                ? { ...snapshotFromPriced(byId[leg.id]), side: leg.side || byId[leg.id].side }
                : leg,
            );
            commit("setLegs", legs);
            trackFeature("betslip_price", "betslip", {
              legs: state.legs.length,
              edge: priced.totals ? priced.totals.edge : null,
            });
            resolve(priced);
          } catch (error) {
            commit("setError", "Couldn't price the slip. Try again in a moment.");
            resolve(null);
          }
          commit("setPricing", false);
        }, 300);
      });
    },
    // Build towards the target odds around the picks already on the slip; the server only takes
    // one off when it has to and says why.
    async build({ state, commit, dispatch, rootGetters }, { targetOdds, date }) {
      const credentials = auth(rootGetters);
      if (!credentials) return null;
      const current = state.legs;
      trackFeature("betslip_build", "betslip", { target: targetOdds, kept: current.length });
      commit("setPricing", true);
      commit("setBuildNotes", []);
      try {
        const response = await API.post("/slip/build", credentials, {
          target_odds: Number(targetOdds),
          max_legs: 4,
          keep: current.map((leg) => leg.id),
          ...(date ? { date } : {}),
        });
        const priced = response.data;
        if (!priced.legs || !priced.legs.length) {
          commit("setError", "No value legs reach that price right now. Try a lower target.");
          commit("setPricing", false);
          return priced;
        }
        commit("setLegs", mergeBuilt(current, priced.legs));
        commit("setBuildNotes", buildNotes(current, priced.legs, priced.removed));
        commit("setPriced", priced);
        commit("setError", "");
        commit("setOpen", true);
        commit("setPricing", false);
        dispatch("reprice");
        return priced;
      } catch (error) {
        commit("setError", "Couldn't build a slip. Try again in a moment.");
        commit("setPricing", false);
        return null;
      }
    },
    async save({ state, rootGetters }) {
      const credentials = auth(rootGetters);
      if (!credentials || !state.legs.length) return null;
      const response = await API.post("/slips", credentials, {
        selections: state.legs.map((leg) => leg.id),
        stake: state.stake,
      });
      trackFeature("betslip_save", "betslip", { legs: state.legs.length });
      return response.data;
    },
    async share({ dispatch }) {
      const saved = await dispatch("save");
      if (!saved || !saved.slip_id) return null;
      trackFeature("betslip_share", "betslip");
      return `${window.location.origin}/slip/${saved.slip_id}`;
    },
    async loadShared(context, id) {
      const response = await API.get(`/slips/shared/${encodeURIComponent(id)}`, {
        ALWAYSIGNOREAUTH: true,
      });
      return response.data;
    },
    // Replace the slip with the given selections (shared slip, ?slip= link).
    prefill({ commit, dispatch }, legs) {
      const clean = (legs || [])
        .filter((leg) => leg && leg.id && String(leg.id).split("|").length === 4)
        .slice(0, MAX_LEGS)
        .map((leg) => ({
          id: leg.id,
          label: leg.label || "",
          match: leg.match || "",
          match_id: leg.match_id || Number(String(leg.id).split("|")[0]),
          kickoff: leg.kickoff || null,
          side: leg.side || null,
        }));
      if (!clean.length) return;
      commit("setBuildNotes", []);
      commit("setLegs", clean);
      commit("setOpen", true);
      dispatch("reprice");
    },
  },
};
